<script setup lang="ts">
import { computed } from 'vue'
import {
  itemScopeAttributes,
  useModelStrings,
  type DetailTemplateProps,
} from '@jantm/model-core'
import type {Certificate} from "@/types/Wiser/certificate.ts";
import DocumentsComponent from "@/components/DocumentsComponent.vue";

const props = withDefaults(defineProps<DetailTemplateProps<Certificate>>(), { headingLevel: 2 })

const { t, core, locale, detailLabel } = useModelStrings(() => props.definition)

const scope = computed(() => itemScopeAttributes(props.definition))
const heading = computed(() => `h${props.headingLevel}` as const)
const regionLabel = computed(() => props.ariaLabel ?? detailLabel.value)
const entityId = computed(() => props.entity?.id ?? 0)
</script>

<template>
  <section
    class="wiser-detail certificate-detail"
    role="region"
    :aria-label="regionLabel"
    :aria-busy="loading && Boolean(entity)"
    v-bind="scope"
  >
    <p
      v-if="error"
      class="wiser-detail__error certificate-detail__error"
      role="alert"
    >
      {{ core('state.error', { fallback: 'Something went wrong.' }) }}
    </p>
    <p
      v-else-if="loading && !entity"
      role="status"
    >
      {{ core('state.loading', { fallback: 'Loading…' }) }}
    </p>
    <p
      v-else-if="!entity"
      role="status"
    >
      {{ core('state.notFound', { fallback: 'Not found.' }) }}
    </p>

    <template v-else>
      <!--
        The brand and model together are the accessible name of this record, so
        they are the heading — not decoration above it. The heading level comes
        from the caller so the page outline stays intact wherever it is used.
      -->
      <component
        :is="heading"
        class="wiser-detail__title certificate-detail__title"
      >
        <span>{{ entity.certificaattitel }} - {{ entity.naam }}</span>
        <a class="btn btn-primary float-right" :href="`/api/wiser/generateCertificate/${entity.id}?print=true`" onclick="this.target='_blank'">Print</a>
        <a class="btn btn-primary float-right" :href="`/api/wiser/generateCertificate/${entity.id}/en?print=true`" onclick="this.target='_blank'">Print EN</a>
      </component>

      <DocumentsComponent :entityId="entity.id" />
    </template>
  </section>
</template>
